import React, { useState, useEffect } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  Cell,
} from "recharts";
import NavRail from "../components/NavRail";
import {
  getStats,
  getWalletBalance,
  getRiskState,
  getEquityCurve,
  getDailyPnl,
  getDistribution,
  getExitReasons,
  getPerfByHour,
  getPerfByDow,
} from "../services/api";

/* ============================================================
   PERFORMANCE — Backoffice bot memecoin Solana (V1)
   Full-screen self-contained · dark terminal · même side-rail
   que le Cockpit. KPIs câblés sur /api/stats. Les 4 graphiques
   (equity curve, PnL/jour, distribution, motifs de sortie) sont
   câblés sur /api/performance/* (recharts). Filtres period /
   mode / shadow + toggle SOL/USD. SOL réel = sell_pnl_sol.
   ============================================================ */

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600;700;800&display=swap');
:root{
  --bg:#0a0b0e;--surface:#14161c;--surface2:#191c23;--border:#1f2329;
  --text:#e6e8ec;--text2:#8a8f98;
  --green:#00e08a;--red:#ff3b5c;--amber:#ffb020;--cyan:#22d3ee;
}
*{box-sizing:border-box;margin:0;padding:0}
.ck,.ck *{font-family:'JetBrains Mono',ui-monospace,monospace;}
.ck{background:var(--bg);color:var(--text);height:100vh;display:flex;overflow:hidden;
  font-size:13px;line-height:1.35;-webkit-font-smoothing:antialiased;}

.sb{width:64px;flex:0 0 64px;border-right:1px solid var(--border);background:#0c0d11;display:flex;
  flex-direction:column;align-items:center;padding:14px 0;gap:6px;}
.sb-logo{width:34px;height:34px;border-radius:8px;background:var(--red);color:#fff;display:flex;
  align-items:center;justify-content:center;font-weight:800;font-size:16px;margin-bottom:14px;
  box-shadow:0 0 14px rgba(255,59,92,.45);}
.sb-item{width:40px;height:40px;border-radius:8px;display:flex;align-items:center;justify-content:center;
  color:var(--text2);cursor:pointer;position:relative;transition:all .12s;font-size:17px;border:none;background:transparent;}
.sb-item:hover{background:var(--surface);color:var(--text);}
.sb-item.on{background:var(--surface);color:var(--green);box-shadow:inset 2px 0 0 var(--green);}
.sb-item .tip{position:absolute;left:52px;top:50%;transform:translateY(-50%);background:#000;
  border:1px solid var(--border);padding:4px 9px;border-radius:6px;font-size:11px;white-space:nowrap;
  opacity:0;pointer-events:none;transition:opacity .12s;z-index:50;color:var(--text);}
.sb-item:hover .tip{opacity:1;}
.sb-spacer{flex:1;}

.main{flex:1;display:flex;flex-direction:column;overflow:hidden;}
.top{display:flex;align-items:center;gap:18px;padding:11px 18px;border-bottom:1px solid var(--border);
  background:#0c0d11;flex:0 0 auto;}
.mode{display:flex;align-items:center;gap:9px;padding:9px 16px;border-radius:9px;font-weight:800;
  font-size:14px;letter-spacing:.04em;}
.mode.live{background:var(--red);color:#fff;box-shadow:0 0 0 1px rgba(255,59,92,.6),0 0 22px rgba(255,59,92,.35);}
.mode.paper{background:rgba(0,224,138,.13);color:var(--green);border:1px solid rgba(0,224,138,.4);}
.dot{width:9px;height:9px;border-radius:50%;background:#fff;animation:pulse 1.3s infinite;}
@keyframes pulse{0%{box-shadow:0 0 0 0 rgba(255,255,255,.7);}70%{box-shadow:0 0 0 7px rgba(255,255,255,0);}100%{box-shadow:0 0 0 0 rgba(255,255,255,0);}}
.mode.paper .dot{background:var(--green);animation:pulseg 2.4s infinite;}
@keyframes pulseg{0%{box-shadow:0 0 0 0 rgba(0,224,138,.5);}70%{box-shadow:0 0 0 6px rgba(0,224,138,0);}100%{box-shadow:0 0 0 0 rgba(0,224,138,0);}}
.wallet{display:flex;flex-direction:column;gap:1px;}
.wallet .lbl{font-size:10px;color:var(--text2);letter-spacing:.12em;text-transform:uppercase;}
.wallet .val{font-size:22px;font-weight:700;}
.delta{display:flex;align-items:center;gap:5px;font-size:13px;font-weight:600;}
.delta.neg{color:var(--red);}.delta.pos{color:var(--green);}
.top-spacer{flex:1;}
.clock{font-size:11px;color:var(--text2);text-align:right;}.clock b{color:var(--text);font-weight:600;}

.page{flex:1;overflow:auto;padding:16px;}
.bar-row{display:flex;align-items:center;justify-content:space-between;margin-bottom:15px;}
.h1{font-size:13px;letter-spacing:.16em;text-transform:uppercase;color:var(--text2);font-weight:600;}
.period{display:flex;gap:4px;background:var(--surface);border:1px solid var(--border);border-radius:9px;padding:3px;}
.period button{background:none;border:none;color:var(--text2);font-family:inherit;font-size:11px;font-weight:600;
  padding:6px 14px;border-radius:6px;cursor:pointer;letter-spacing:.05em;transition:all .12s;}
.period button.on{background:var(--surface2);color:var(--text);box-shadow:inset 0 0 0 1px var(--border);}
.period button:hover{color:var(--text);}

.kpis{display:grid;grid-template-columns:repeat(7,1fr);gap:10px;margin-bottom:16px;}
@media(max-width:1300px){.kpis{grid-template-columns:repeat(4,1fr);}}
@media(max-width:1100px){.kpis{grid-template-columns:repeat(3,1fr);}}
@media(max-width:640px){.kpis{grid-template-columns:repeat(2,1fr);}}
.kpi{background:var(--surface);border:1px solid var(--border);border-radius:10px;padding:12px 13px;}
.kpi .k{font-size:9.5px;color:var(--text2);letter-spacing:.08em;text-transform:uppercase;}
.kpi .v{font-size:20px;font-weight:800;margin-top:5px;letter-spacing:-.01em;}
.kpi .v.pos{color:var(--green);}.kpi .v.neg{color:var(--red);}.kpi .v.cy{color:var(--cyan);}
.kpi .v.muted{color:var(--text2);}
.kpi .sub{font-size:10px;color:var(--text2);margin-top:3px;}

.grid2{display:grid;grid-template-columns:2fr 1fr;gap:12px;margin-bottom:12px;}
@media(max-width:980px){.grid2{grid-template-columns:1fr;}}
.card{background:var(--surface);border:1px solid var(--border);border-radius:12px;padding:14px;}
.card-ti{font-size:11px;letter-spacing:.14em;text-transform:uppercase;color:var(--text2);font-weight:600;
  margin-bottom:12px;display:flex;justify-content:space-between;align-items:center;}
.card-ti .r{font-size:11px;letter-spacing:0;text-transform:none;}
.card-ti .r b.pos{color:var(--green);}.card-ti .r b.neg{color:var(--red);}

.dist-row{display:flex;align-items:center;gap:10px;margin-bottom:9px;}
.dist-lbl{width:62px;font-size:11px;color:var(--text2);text-align:right;}
.dist-bar{flex:1;height:18px;background:#0a0b0e;border-radius:5px;overflow:hidden;border:1px solid var(--border);}
.dist-fill{height:100%;border-radius:4px;}
.dist-val{width:48px;font-size:11px;font-weight:700;text-align:right;}

.exit-row{display:flex;align-items:center;gap:10px;margin-bottom:11px;}
.exit-tag{width:78px;font-size:10px;font-weight:700;padding:3px 8px;border-radius:5px;text-align:center;}
.exit-tag.tp{background:rgba(0,224,138,.14);color:var(--green);}
.exit-tag.sl{background:rgba(255,59,92,.14);color:var(--red);}
.exit-tag.timeout{background:rgba(138,143,152,.16);color:var(--text2);}
.exit-tag.trail{background:rgba(34,211,238,.14);color:var(--cyan);}
.exit-tag.manual{background:rgba(255,176,32,.16);color:var(--amber);}
.exit-bar{flex:1;height:8px;background:#0a0b0e;border-radius:4px;overflow:hidden;}
.exit-fill{height:100%;}
.exit-pct{width:42px;font-size:11px;font-weight:700;text-align:right;color:var(--text);}

.nodata{display:flex;align-items:center;justify-content:center;min-height:120px;font-size:11.5px;
  color:var(--text2);text-align:center;border:1px dashed var(--border);border-radius:8px;padding:14px;line-height:1.6;}
.err-banner{font-size:11px;color:var(--amber);margin-bottom:12px;padding:8px 12px;border-radius:8px;
  background:rgba(255,176,32,.10);border:1px solid rgba(255,176,32,.35);}

/* contrôles (mode / shadow / unité) — réutilisent le look .period */
.ctrls{display:flex;align-items:center;gap:14px;flex-wrap:wrap;}
.ctrl-grp{display:flex;align-items:center;gap:7px;}
.ctrl-lbl{font-size:9.5px;letter-spacing:.1em;text-transform:uppercase;color:var(--text2);font-weight:600;}

/* recharts tooltip terminal */
.rc-tip{background:#000;border:1px solid var(--border);border-radius:6px;padding:7px 10px;font-size:11px;
  font-family:'JetBrains Mono',monospace;color:var(--text);}
.rc-tip .t{color:var(--text2);font-size:10px;margin-bottom:3px;}
.rc-tip .v.pos{color:var(--green);}.rc-tip .v.neg{color:var(--red);}.rc-tip .v.cy{color:var(--cyan);}
.recharts-cartesian-axis-tick-value{font-family:'JetBrains Mono',monospace;}

/* table motifs de sortie */
.ex-tbl{width:100%;border-collapse:collapse;font-size:11px;}
.ex-tbl th{text-align:left;color:var(--text2);font-weight:600;font-size:9.5px;letter-spacing:.06em;
  text-transform:uppercase;padding:4px 6px;border-bottom:1px solid var(--border);}
.ex-tbl th.num,.ex-tbl td.num{text-align:right;}
.ex-tbl td{padding:5px 6px;border-bottom:1px solid var(--surface2);}
.ex-tbl tr:last-child td{border-bottom:none;}
.ex-tbl .rs{color:var(--text);font-weight:600;}
.ex-tbl .pos{color:var(--green);}.ex-tbl .neg{color:var(--red);}
.ex-bar{height:6px;border-radius:3px;background:var(--cyan);min-width:2px;}
.chart-wrap{width:100%;height:230px;}
.chart-loading{display:flex;align-items:center;justify-content:center;height:230px;color:var(--text2);font-size:11px;}
`;

/* PERIODS : libellés UI → param `period` des endpoints /performance/*.
   /api/stats reste all-time (pas de param fenêtre) → les KPIs getStats
   sont cumulés ; seuls les 4 graphiques réagissent à la période. */
const PERIODS = ["24H", "7J", "30J", "ALL"];
const PERIOD_MAP = { "24H": "24h", "7J": "7d", "30J": "30d", ALL: "all" };
const MODE_OPTS = [["all", "TOUT"], ["live", "LIVE"], ["paper", "PAPER"]];
const SHADOW_OPTS = [["exclude", "RÉEL"], ["only", "SHADOW"], ["all", "TOUT"]];

const C = { green: "#00e08a", red: "#ff3b5c", amber: "#ffb020", cyan: "#22d3ee", text2: "#8a8f98", border: "#1f2329" };

/* nombre fr, n décimales adaptatives (USD = entier, SOL = 3) */
const fmtNum = (v, d = 3) =>
  v === null || v === undefined || isNaN(v)
    ? "—"
    : Number(v).toLocaleString("fr-FR", { maximumFractionDigits: d, minimumFractionDigits: 0 });

/* format axe temps selon granularité renvoyée par l'API */
const fmtAxisTime = (iso, gran) => {
  const dt = new Date(iso);
  if (isNaN(dt.getTime())) return "";
  return gran === "hour"
    ? dt.toLocaleString("fr-FR", { day: "2-digit", hour: "2-digit", minute: "2-digit" })
    : dt.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" });
};

/* fmt helpers — alignés sur le Cockpit (USD car realized_pnl est en USD) */
const fmtUsd = (v) =>
  v === null || v === undefined ? "—" : (v >= 0 ? "+" : "") + "$" + Math.round(v).toLocaleString("fr-FR");
const fmtSol = (v, d = 4) =>
  v === null || v === undefined ? "—" : Number(v).toFixed(d);
const fmtPct = (v) => (v === null || v === undefined ? "—" : `${Number(v).toFixed(1)}%`);
const fmtHold = (hours) => {
  if (hours === null || hours === undefined) return "—";
  const s = hours * 3600;
  if (s < 60) return `${Math.round(s)}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m${String(Math.round(s % 60)).padStart(2, "0")}`;
  return `${hours.toFixed(1)}h`;
};

/* Tooltip equity/PnL : valeur SOL ou USD selon `unitLabel` */
function ValueTooltip({ active, payload, label, unitLabel, gran }) {
  if (!active || !payload || !payload.length) return null;
  const v = payload[0].value;
  const cls = v == null ? "" : v >= 0 ? "pos" : "neg";
  return (
    <div className="rc-tip">
      <div className="t">{gran ? fmtAxisTime(label, gran) : label}</div>
      <div className={`v ${cls}`}>{(v >= 0 ? "+" : "") + fmtNum(v, unitLabel.startsWith("USD") ? 0 : 3)} {unitLabel}</div>
    </div>
  );
}

/* Tooltip distribution : count + bornes du bucket (%) */
function DistTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0].payload;
  return (
    <div className="rc-tip">
      <div className="t">{d.label} %</div>
      <div className="v cy">{d.count} trades</div>
    </div>
  );
}

/* Tooltip heure/jour : PnL (SOL/USD) + trades + win rate % */
function BucketTooltip({ active, payload, unitLabel }) {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0].payload;
  const v = d.val;
  const cls = v == null ? "" : v >= 0 ? "pos" : "neg";
  return (
    <div className="rc-tip">
      <div className="t">{d.label}</div>
      <div className={`v ${cls}`}>
        {(v == null ? "—" : (v >= 0 ? "+" : "") + fmtNum(v, unitLabel.startsWith("USD") ? 0 : 3))} {unitLabel}
      </div>
      <div className="t" style={{ marginTop: 4, marginBottom: 0 }}>
        {d.trades} trades · WR {d.win_rate == null ? "—" : `${Number(d.win_rate).toFixed(0)}%`}
      </div>
    </div>
  );
}

export default function PerformancePage() {
  const [period, setPeriod] = useState("ALL");
  const [modeF, setModeF] = useState("all");        // all | live | paper
  const [shadow, setShadow] = useState("exclude");   // exclude | only | all
  const [unit, setUnit] = useState("SOL");           // SOL | USD
  const [clock, setClock] = useState(new Date());

  const apiPeriod = PERIOD_MAP[period] || "all";
  const chartFilters = { period: apiPeriod, mode: modeF, shadow };

  // VRAIES données — même pattern react-query que le Cockpit.
  const stats = useQuery({
    queryKey: ["perf", "stats"],
    queryFn: getStats,
    refetchInterval: 15000,
  });
  const wallet = useQuery({
    queryKey: ["perf", "wallet"],
    queryFn: getWalletBalance,
    refetchInterval: 10000,
  });
  const risk = useQuery({
    queryKey: ["perf", "risk"],
    queryFn: getRiskState,
    refetchInterval: 5000,
  });

  // Graphiques — refetch auto au changement de period/mode/shadow (queryKey).
  const equity = useQuery({
    queryKey: ["perf", "equity", apiPeriod, modeF, shadow],
    queryFn: () => getEquityCurve(chartFilters),
    refetchInterval: 30000,
    placeholderData: keepPreviousData,
  });
  const daily = useQuery({
    queryKey: ["perf", "daily", apiPeriod, modeF, shadow],
    queryFn: () => getDailyPnl(chartFilters),
    refetchInterval: 30000,
    placeholderData: keepPreviousData,
  });
  const dist = useQuery({
    queryKey: ["perf", "dist", apiPeriod, modeF, shadow],
    queryFn: () => getDistribution(chartFilters),
    refetchInterval: 30000,
    placeholderData: keepPreviousData,
  });
  const exits = useQuery({
    queryKey: ["perf", "exits", apiPeriod, modeF, shadow],
    queryFn: () => getExitReasons(chartFilters),
    refetchInterval: 30000,
    placeholderData: keepPreviousData,
  });
  const byHour = useQuery({
    queryKey: ["perf", "byHour", apiPeriod, modeF, shadow],
    queryFn: () => getPerfByHour(chartFilters),
    refetchInterval: 30000,
    placeholderData: keepPreviousData,
  });
  const byDow = useQuery({
    queryKey: ["perf", "byDow", apiPeriod, modeF, shadow],
    queryFn: () => getPerfByDow(chartFilters),
    refetchInterval: 30000,
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    const id = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const r = risk.data || {};
  const mode = (r.mode || "paper").toUpperCase();
  const isLive = mode === "LIVE";

  const w = wallet.data || {};
  const cp = stats.data?.closed_positions || {};
  const ov = stats.data?.overall || {};
  const ap = stats.data?.active_positions || {};

  // KPIs câblés sur le vrai /api/stats
  const realizedPnl = cp.realized_pnl;                 // USD
  const winRate = cp.win_rate;                         // %
  const trades = cp.count;                             // closed trades
  const avgHoldHours = ov.avg_holding_hours;           // heures
  const activeCount = ap.count;
  const bestTrade = ov.best_trade;
  const worstTrade = ov.worst_trade;

  const t = clock.toLocaleTimeString("fr-FR", { hour12: false, timeZone: "Europe/Paris" });
  const statsErr = stats.isError;

  // ---- Graphiques : conversion SOL/USD ----
  const isUsd = unit === "USD";
  const unitLbl = isUsd ? "USD ≈" : "SOL";
  // chaque endpoint renvoie son propre sol_usd_rate ; on prend celui de la réponse concernée
  const conv = (sol, rate) =>
    sol === null || sol === undefined ? null : isUsd ? Number(sol) * (rate || 0) : Number(sol);

  const eq = equity.data || {};
  const eqPoints = (eq.points || []).map((p) => ({ t: p.t, val: conv(p.cum_sol, eq.sol_usd_rate) }));
  const eqLast = eqPoints.length ? eqPoints[eqPoints.length - 1].val : null;
  const eqColor = eqLast == null ? C.text2 : eqLast >= 0 ? C.green : C.red;
  const maxDd = conv(eq.max_drawdown_sol, eq.sol_usd_rate);
  const profitFactor = eq.profit_factor;

  const dailyDays = (daily.data?.days || []).map((d) => ({
    date: d.date,
    val: conv(d.pnl_sol, daily.data?.sol_usd_rate),
    trades: d.trades,
    wins: d.wins,
  }));

  const distBuckets = dist.data?.buckets || [];
  const exitReasons = exits.data?.reasons || [];
  const exitRate = exits.data?.sol_usd_rate;
  const maxExitCount = exitReasons.reduce((m, r) => Math.max(m, r.count || 0), 0) || 1;

  // couleur d'un bucket de distribution selon ses bornes (%)
  const distColor = (b) => {
    if (b.hi != null && b.hi <= 0) return C.red;
    if (b.lo != null && b.lo >= 0) return C.green;
    return C.amber;
  };

  // Performance par heure (24 barres) — pnl SOL/USD selon toggle
  const hourBars = (byHour.data?.hours || []).map((h) => ({
    label: `${String(h.hour).padStart(2, "0")}h`,
    val: conv(h.pnl_sol, byHour.data?.sol_usd_rate),
    trades: h.trades,
    win_rate: h.win_rate,
  }));
  // Performance par jour de semaine (7 barres, déjà Lun→Dim côté backend)
  const dowBars = (byDow.data?.days || []).map((d) => ({
    label: d.label,
    val: conv(d.pnl_sol, byDow.data?.sol_usd_rate),
    trades: d.trades,
    win_rate: d.win_rate,
  }));

  return (
    <div className="ck">
      <style>{CSS}</style>

      <NavRail />

      <div className="main">
        <div className="top">
          {isLive ? (
            <div className="mode live"><span className="dot" /> LIVE — CAPITAL RÉEL</div>
          ) : (
            <div className="mode paper"><span className="dot" /> PAPER</div>
          )}
          <div className="wallet">
            <span className="lbl">Wallet</span>
            <span className="val">{w.configured === false ? "non config." : `${fmtSol(w.sol)} SOL`}</span>
          </div>
          {w.session_delta_sol != null && (
            <div className={`delta ${w.session_delta_sol < 0 ? "neg" : "pos"}`}>
              {w.session_delta_sol < 0 ? "▼" : "▲"}{w.session_delta_sol >= 0 ? "+" : ""}{fmtSol(w.session_delta_sol)} SOL
              <span style={{ color: "var(--text2)", fontWeight: 400, fontSize: 10 }}>session</span>
            </div>
          )}
          <div className="top-spacer" />
          <div className="clock"><b>{t}</b><br />Europe/Paris</div>
        </div>

        <div className="page">
          <div className="bar-row">
            <span className="h1">Performance</span>
            <div className="ctrls">
              <div className="ctrl-grp">
                <span className="ctrl-lbl">Période</span>
                <div className="period">
                  {PERIODS.map((p) => (
                    <button key={p} className={period === p ? "on" : ""} onClick={() => setPeriod(p)}>{p}</button>
                  ))}
                </div>
              </div>
              <div className="ctrl-grp">
                <span className="ctrl-lbl">Mode</span>
                <div className="period">
                  {MODE_OPTS.map(([v, l]) => (
                    <button key={v} className={modeF === v ? "on" : ""} onClick={() => setModeF(v)}>{l}</button>
                  ))}
                </div>
              </div>
              <div className="ctrl-grp">
                <span className="ctrl-lbl">Shadow</span>
                <div className="period">
                  {SHADOW_OPTS.map(([v, l]) => (
                    <button key={v} className={shadow === v ? "on" : ""} onClick={() => setShadow(v)}>{l}</button>
                  ))}
                </div>
              </div>
              <div className="ctrl-grp">
                <span className="ctrl-lbl">Unité</span>
                <div className="period">
                  {["SOL", "USD"].map((u) => (
                    <button key={u} className={unit === u ? "on" : ""} onClick={() => setUnit(u)}>{u}</button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {statsErr && (
            <div className="err-banner">⚠ /api/stats indisponible — KPIs en attente de données.</div>
          )}

          {/* KPIs — câblés sur /api/stats (closed_positions / overall) */}
          <div className="kpis">
            <div className="kpi">
              <div className="k">PnL réalisé</div>
              <div className={`v ${realizedPnl == null ? "muted" : realizedPnl >= 0 ? "pos" : "neg"}`}>{fmtUsd(realizedPnl)}</div>
              <div className="sub">cumulé · {fmtPct(cp.realized_pnl_percent)}</div>
            </div>
            <div className="kpi">
              <div className="k">Win rate</div>
              <div className="v">{winRate == null ? "—" : `${winRate}%`}</div>
              <div className="sub">{cp.winning_trades ?? "—"}W / {cp.losing_trades ?? "—"}L</div>
            </div>
            <div className="kpi">
              <div className="k">Trades</div>
              <div className="v">{trades == null ? "—" : trades.toLocaleString("fr-FR")}</div>
              <div className="sub">fermés · {activeCount ?? "—"} actifs</div>
            </div>
            <div className="kpi">
              <div className="k">Profit factor</div>
              <div className={`v ${profitFactor == null ? "muted" : profitFactor >= 1 ? "pos" : "neg"}`}>
                {profitFactor == null ? "—" : Number(profitFactor).toFixed(2)}
              </div>
              <div className="sub">gains / pertes · {period}</div>
            </div>
            <div className="kpi">
              <div className="k">Max drawdown</div>
              <div className={`v ${maxDd == null ? "muted" : "neg"}`}>
                {maxDd == null ? "—" : "−" + fmtNum(Math.abs(maxDd), isUsd ? 0 : 3)}
              </div>
              <div className="sub">
                {unitLbl}{eq.max_drawdown_pct != null ? ` · ${fmtPct(eq.max_drawdown_pct)}` : ""}
              </div>
            </div>
            <div className="kpi">
              <div className="k">Avg hold</div>
              <div className="v cy">{fmtHold(avgHoldHours)}</div>
              <div className="sub">overall.avg_holding_hours</div>
            </div>
            <div className="kpi">
              <div className="k">Best / Worst</div>
              <div className={`v ${bestTrade == null ? "muted" : "pos"}`} style={{ fontSize: 15 }}>{fmtUsd(bestTrade)}</div>
              <div className="sub">worst <b style={{ color: "var(--red)" }}>{fmtUsd(worstTrade)}</b></div>
            </div>
          </div>

          {/* equity curve + motifs de sortie */}
          <div className="grid2">
            <div className="card">
              <div className="card-ti">
                Equity curve · {period} · {unitLbl}
                <span className="r">
                  cumul <b className={eqLast == null ? "" : eqLast >= 0 ? "pos" : "neg"}>
                    {eqLast == null ? "—" : (eqLast >= 0 ? "+" : "") + fmtNum(eqLast, isUsd ? 0 : 3)}
                  </b>{" · PF "}<b>{profitFactor == null ? "—" : Number(profitFactor).toFixed(2)}</b>
                </span>
              </div>
              {equity.isError ? (
                <div className="nodata">Equity curve indisponible — /api/performance/equity-curve en erreur.</div>
              ) : equity.isLoading && !equity.data ? (
                <div className="chart-loading">chargement…</div>
              ) : eqPoints.length === 0 ? (
                <div className="nodata">Aucun trade fermé sur la fenêtre sélectionnée.</div>
              ) : (
                <div className="chart-wrap">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={eqPoints} margin={{ top: 6, right: 12, left: 0, bottom: 0 }}>
                      <CartesianGrid stroke={C.border} vertical={false} />
                      <XAxis dataKey="t" tickFormatter={(v) => fmtAxisTime(v, eq.granularity)}
                        stroke={C.text2} tick={{ fontSize: 10, fill: C.text2 }} minTickGap={36} />
                      <YAxis stroke={C.text2} tick={{ fontSize: 10, fill: C.text2 }} width={52}
                        tickFormatter={(v) => fmtNum(v, isUsd ? 0 : 2)} />
                      <Tooltip content={<ValueTooltip unitLabel={unitLbl} gran={eq.granularity} />} />
                      <ReferenceLine y={0} stroke={C.border} />
                      <Line type="monotone" dataKey="val" stroke={eqColor} dot={false} strokeWidth={2} isAnimationActive={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
            <div className="card">
              <div className="card-ti">
                Motifs de sortie
                <span className="r" style={{ color: "var(--text2)" }}>{exitReasons.length} motifs</span>
              </div>
              {exits.isError ? (
                <div className="nodata">Motifs indisponibles — /api/performance/exit-reasons en erreur.</div>
              ) : exits.isLoading && !exits.data ? (
                <div className="chart-loading">chargement…</div>
              ) : exitReasons.length === 0 ? (
                <div className="nodata">Aucun motif sur la fenêtre sélectionnée.</div>
              ) : (
                <table className="ex-tbl">
                  <thead>
                    <tr>
                      <th>Motif</th>
                      <th className="num">N</th>
                      <th className="num">PnL {unitLbl}</th>
                      <th className="num">Avg%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {exitReasons.map((rsn) => {
                      const pnl = conv(rsn.pnl_sol, exitRate);
                      return (
                        <tr key={rsn.reason}>
                          <td className="rs" title={rsn.reason}>
                            <div>{rsn.reason}</div>
                            <div className="ex-bar" style={{ width: `${Math.max(2, (rsn.count / maxExitCount) * 100)}%` }} />
                          </td>
                          <td className="num">{rsn.count}</td>
                          <td className={`num ${pnl == null ? "" : pnl >= 0 ? "pos" : "neg"}`}>
                            {pnl == null ? "—" : (pnl >= 0 ? "+" : "") + fmtNum(pnl, isUsd ? 0 : 3)}
                          </td>
                          <td className={`num ${rsn.avg_pct == null ? "" : rsn.avg_pct >= 0 ? "pos" : "neg"}`}>
                            {fmtPct(rsn.avg_pct)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* PnL quotidien + distribution */}
          <div className="grid2">
            <div className="card">
              <div className="card-ti">
                PnL quotidien · {unitLbl}
                <span className="r" style={{ color: "var(--text2)" }}>{dailyDays.length} j</span>
              </div>
              {daily.isError ? (
                <div className="nodata">PnL/jour indisponible — /api/performance/daily-pnl en erreur.</div>
              ) : daily.isLoading && !daily.data ? (
                <div className="chart-loading">chargement…</div>
              ) : dailyDays.length === 0 ? (
                <div className="nodata">Aucun jour avec trade sur la fenêtre sélectionnée.</div>
              ) : (
                <div className="chart-wrap">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={dailyDays} margin={{ top: 6, right: 12, left: 0, bottom: 0 }}>
                      <CartesianGrid stroke={C.border} vertical={false} />
                      <XAxis dataKey="date" stroke={C.text2} tick={{ fontSize: 10, fill: C.text2 }} minTickGap={24}
                        tickFormatter={(v) => { const d = new Date(v); return isNaN(d) ? v : d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" }); }} />
                      <YAxis stroke={C.text2} tick={{ fontSize: 10, fill: C.text2 }} width={52}
                        tickFormatter={(v) => fmtNum(v, isUsd ? 0 : 2)} />
                      <Tooltip content={<ValueTooltip unitLabel={unitLbl} />} cursor={{ fill: "rgba(255,255,255,.04)" }} />
                      <ReferenceLine y={0} stroke={C.border} />
                      <Bar dataKey="val" isAnimationActive={false}>
                        {dailyDays.map((d, i) => (
                          <Cell key={i} fill={d.val >= 0 ? C.green : C.red} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
            <div className="card">
              <div className="card-ti">
                Distribution PnL / trade
                <span className="r" style={{ color: "var(--text2)" }}>en % (pas de toggle)</span>
              </div>
              {dist.isError ? (
                <div className="nodata">Distribution indisponible — /api/performance/distribution en erreur.</div>
              ) : dist.isLoading && !dist.data ? (
                <div className="chart-loading">chargement…</div>
              ) : distBuckets.length === 0 ? (
                <div className="nodata">Aucun trade sur la fenêtre sélectionnée.</div>
              ) : (
                <div className="chart-wrap">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={distBuckets} margin={{ top: 6, right: 12, left: 0, bottom: 0 }}>
                      <CartesianGrid stroke={C.border} vertical={false} />
                      <XAxis dataKey="label" stroke={C.text2} tick={{ fontSize: 9, fill: C.text2 }} interval={0} angle={-30} textAnchor="end" height={48} />
                      <YAxis stroke={C.text2} tick={{ fontSize: 10, fill: C.text2 }} width={42} allowDecimals={false} />
                      <Tooltip content={<DistTooltip />} cursor={{ fill: "rgba(255,255,255,.04)" }} />
                      <Bar dataKey="count" isAnimationActive={false}>
                        {distBuckets.map((b, i) => (
                          <Cell key={i} fill={distColor(b)} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </div>

          {/* Performance par heure (24) + par jour de semaine (7) */}
          <div className="grid2">
            <div className="card">
              <div className="card-ti">
                Performance par heure · {unitLbl}
                <span className="r" style={{ color: "var(--text2)" }}>Europe/Paris</span>
              </div>
              {byHour.isError ? (
                <div className="nodata">Perf/heure indisponible — /api/performance/by-hour en erreur.</div>
              ) : byHour.isLoading && !byHour.data ? (
                <div className="chart-loading">chargement…</div>
              ) : hourBars.length === 0 ? (
                <div className="nodata">Aucun trade fermé sur la fenêtre sélectionnée.</div>
              ) : (
                <div className="chart-wrap">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={hourBars} margin={{ top: 6, right: 12, left: 0, bottom: 0 }}>
                      <CartesianGrid stroke={C.border} vertical={false} />
                      <XAxis dataKey="label" stroke={C.text2} tick={{ fontSize: 9, fill: C.text2 }} interval={1} />
                      <YAxis stroke={C.text2} tick={{ fontSize: 10, fill: C.text2 }} width={52}
                        tickFormatter={(v) => fmtNum(v, isUsd ? 0 : 2)} />
                      <Tooltip content={<BucketTooltip unitLabel={unitLbl} />} cursor={{ fill: "rgba(255,255,255,.04)" }} />
                      <ReferenceLine y={0} stroke={C.border} />
                      <Bar dataKey="val" isAnimationActive={false}>
                        {hourBars.map((h, i) => (
                          <Cell key={i} fill={h.val >= 0 ? C.green : C.red} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
            <div className="card">
              <div className="card-ti">
                Performance par jour · {unitLbl}
                <span className="r" style={{ color: "var(--text2)" }}>Europe/Paris</span>
              </div>
              {byDow.isError ? (
                <div className="nodata">Perf/jour indisponible — /api/performance/by-dow en erreur.</div>
              ) : byDow.isLoading && !byDow.data ? (
                <div className="chart-loading">chargement…</div>
              ) : dowBars.length === 0 ? (
                <div className="nodata">Aucun trade fermé sur la fenêtre sélectionnée.</div>
              ) : (
                <div className="chart-wrap">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={dowBars} margin={{ top: 6, right: 12, left: 0, bottom: 0 }}>
                      <CartesianGrid stroke={C.border} vertical={false} />
                      <XAxis dataKey="label" stroke={C.text2} tick={{ fontSize: 10, fill: C.text2 }} interval={0} />
                      <YAxis stroke={C.text2} tick={{ fontSize: 10, fill: C.text2 }} width={52}
                        tickFormatter={(v) => fmtNum(v, isUsd ? 0 : 2)} />
                      <Tooltip content={<BucketTooltip unitLabel={unitLbl} />} cursor={{ fill: "rgba(255,255,255,.04)" }} />
                      <ReferenceLine y={0} stroke={C.border} />
                      <Bar dataKey="val" isAnimationActive={false}>
                        {dowBars.map((d, i) => (
                          <Cell key={i} fill={d.val >= 0 ? C.green : C.red} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
